/**
 * EventoRepository - Repositorio unificado para Eventos
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 */

import { v4 as uuidv4 } from 'uuid';
import { IEventoRepository, FiltroEventos } from '../interfaces/IEventoRepository';
import { Evento } from '../interfaces/entities';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter, WhereClause } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';

/**
 * Repositorio unificado de Eventos
 */
export class EventoRepository extends BaseRepository<Evento, string> implements IEventoRepository {
  private profileRepo: IProfileRepository | null = null;

  /** Clause reutilizable para excluir soft-deletes (is_deleted=false). */
  private readonly NOT_DELETED: WhereClause = { field: 'is_deleted', operator: 'equals', value: false };

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'eventos', 'eventos');

    // Habilitar funcionalidades de BaseRepository
    this.supportsSoftDelete = true;
    this.autoTimestamp = true;

    if (profileRepo) {
      this.profileRepo = profileRepo;
      this.setProfileRepository(profileRepo);
    }
  }

  async add(data: Omit<Evento, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();
    const profile = await this.getCurrentProfile();

    const record: Evento = {
      ...data,
      id,
      user_id: data.user_id || profile?.user_id || '',
      empresa_id: data.empresa_id || profile?.empresa_activa_id || '',
      created_at: data.created_at || now,
      updated_at: now,
      is_deleted: false
    };

    await this.db.insert('eventos', record);
    this.emitSync('INSERT', record);
    return id;
  }

  async update(data: Partial<Evento> & { id: string }): Promise<void> {
    const updatedData = {
      ...data,
      updated_at: this.getCurrentTimestamp()
    };
    await this.db.update('eventos', data.id, updatedData);
    this.emitSync('UPDATE', updatedData as any);
  }

  async delete(id: string): Promise<void> {
    await this.db.delete('eventos', id);
    this.emitSync('DELETE', id);
  }

  async getByFiltros(filtros: FiltroEventos): Promise<Evento[]> {
    const clauses: WhereClause[] = [this.NOT_DELETED];

    if (filtros.fecha_inicio) {
      clauses.push({
        field: 'date',
        operator: 'greaterThanOrEqual',
        value: filtros.fecha_inicio
      });
    }

    if (filtros.fecha_fin) {
      clauses.push({
        field: 'date',
        operator: 'lessThanOrEqual',
        value: filtros.fecha_fin
      });
    }

    if (filtros.category) {
      clauses.push({
        field: 'category',
        operator: 'equals',
        value: filtros.category
      });
    }

    if (filtros.user_id) {
      clauses.push({
        field: 'user_id',
        operator: 'equals',
        value: filtros.user_id
      });
    }

    if (filtros.empresa_id) {
      clauses.push({
        field: 'empresa_id',
        operator: 'equals',
        value: filtros.empresa_id
      });
    }

    return this.db.queryMultiple<Evento>('eventos', clauses, {
      column: 'date',
      direction: 'asc'
    });
  }

  async getByFecha(fecha: string): Promise<Evento[]> {
    return this.db.queryMultiple<Evento>('eventos', [
      this.NOT_DELETED,
      { field: 'date', operator: 'equals', value: fecha }
    ]);
  }

  async getByPeriodo(fechaInicio: string, fechaFin: string): Promise<Evento[]> {
    return this.db.queryMultiple<Evento>('eventos', [
      this.NOT_DELETED,
      {
        field: 'date',
        operator: 'between',
        value: [fechaInicio, fechaFin]
      }
    ], { column: 'date', direction: 'asc' });
  }

  async getByCategoria(categoria: string): Promise<Evento[]> {
    return this.db.queryMultiple<Evento>('eventos', [
      this.NOT_DELETED,
      { field: 'category', operator: 'equals', value: categoria }
    ]);
  }

  async getByUsuario(userId: string): Promise<Evento[]> {
    return this.db.queryMultiple<Evento>('eventos', [
      this.NOT_DELETED,
      { field: 'user_id', operator: 'equals', value: userId }
    ]);
  }

  async getByEmpresa(empresaId: string): Promise<Evento[]> {
    return this.db.queryMultiple<Evento>('eventos', [
      this.NOT_DELETED,
      { field: 'empresa_id', operator: 'equals', value: empresaId }
    ]);
  }

  async getPublicosByEmpresa(empresaId: string): Promise<Evento[]> {
    return this.db.queryMultiple<Evento>('eventos', [
      this.NOT_DELETED,
      { field: 'empresa_id', operator: 'equals', value: empresaId },
      { field: 'is_public', operator: 'equals', value: true }
    ]);
  }

  async markAsCompleted(id: string): Promise<void> {
    await this.db.update('eventos', id, { completed: true });
    this.emitSync('UPDATE', { id, completed: true } as any);
  }

  // ==================== Bulk Operations ====================
  // Estos métodos ahora vienen de BaseRepository

  // ==================== Fin ====================

  setProfileRepository(profileRepo: IProfileRepository): void {
    this.profileRepo = profileRepo;
    super.setProfileRepository(profileRepo);
  }
}
